import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import { privateMeta } from '@/lib/seo/site';
import { useSeoHead } from '@/lib/seo/useSeoHead';

import './ErrorPage.css';

// CTA can be a navigation link (default for 404/500) or an action button
// (used by Page403 when an authenticated user with a stale/invalid
// session needs a signOut + navigate combo to break out of a 403 loop).
type ErrorPageCta =
  | { ctaLabel: string; ctaTo: string; onCtaClick?: never }
  | { ctaLabel: string; ctaTo?: never; onCtaClick: () => void };

export type ErrorPageProps = {
  code: string;
  title: string;
  body: string;
  meta?: ReactNode;
} & ErrorPageCta;

// Editorial system page — bronze hairline numeral + serif italic title +
// single bronze CTA link/button styled as primary button.
export function ErrorPage({
  code,
  title,
  body,
  meta,
  ctaLabel,
  ctaTo,
  onCtaClick,
}: ErrorPageProps) {
  // System pages must never be indexed — a CSR 404/403/500 answers HTTP 200,
  // so the noindex directive is the only signal crawlers get.
  useSeoHead(privateMeta(`${code} — ${title}`));
  return (
    <main id="main-content" tabIndex={-1} className="error-page">
      <div className="error-page__inner">
        <div className="error-page__code" aria-hidden="true">
          {code}
        </div>
        <h1 className="error-page__title">{title}</h1>
        <p className="error-page__body">{body}</p>
        {meta ? <div className="error-page__meta">{meta}</div> : null}
        {onCtaClick ? (
          <button type="button" onClick={onCtaClick} className="btn btn--primary btn--lg">
            {ctaLabel}
          </button>
        ) : (
          <Link to={ctaTo!} className="btn btn--primary btn--lg">
            {ctaLabel}
          </Link>
        )}
      </div>
    </main>
  );
}
